import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {MatIconModule} from '@angular/material/icon';
import {LoadingService} from "projects/nter-ui-kit/src/lib/framework/util/service/loading.service";
import {AuthGuardService} from "projects/admin-portal/src/app/guards/route/auth.guard.service";
import {NavigationUtilsService} from "projects/nter-ui-kit/src/lib/framework/util/service/navigation-utils.service";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {AsyncPipe, NgIf} from "@angular/common";
import {AdminContextService} from "@app/security/admin-context.service";
import {AdminAuthenticationService} from "@app/security/admin-authentication.service";
import {AdminAuthorizationService} from "@app/security/admin-authorization.service";
import {MatCardModule} from "@angular/material/card";
import {CenteredCardLayoutComponent} from "src/app/components/centered-card-layout/centered-card-layout.component";
import {MatProgressBar} from "@angular/material/progress-bar";
import {CustomIconService} from "@app/config/custom-icon.service";
import {ProcessingDialogService} from "@ui-webkit/components/processing-dialog/processing-dialog.service";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    AsyncPipe,
    NgIf,
    MatCardModule,
    CenteredCardLayoutComponent,
    MatProgressBar
  ],
  standalone: true
})
export class LoginComponent implements OnDestroy {
  readonly clientId: string = AdminContextService.CLIENT_ID;
  loginForm: FormGroup;
  loading: boolean = false;
  hidePassword = true;
  buttonText: string = 'Login';

  @Input() forgotPassword!: boolean;
  @Output() forgotPasswordChange = new EventEmitter<boolean>();


  constructor(private authenticationService: AdminAuthenticationService,
              private route: ActivatedRoute,
              private router: Router,
              public loader: LoadingService,
              private authGuard: AuthGuardService,
              private _utilService: NavigationUtilsService,
              private authorizationService: AdminAuthorizationService,
              private iconService: CustomIconService,
              private procDialogService: ProcessingDialogService) {
    this.iconService.loadSvgIcons(['icon-show-password', 'icon-hide-password']);

    this.loginForm = new FormGroup({
      email: new FormControl({value: '', disabled: this.loading}, {
        validators: [Validators.required, Validators.email],
      }),
      password: new FormControl({value: '', disabled: this.loading}, {validators: [Validators.required]}),
    });
  }

  userForgotPassword(): void {
    this.forgotPassword = true;
    this.forgotPasswordChange.emit(this.forgotPassword);
    this.router.navigate([`${this.clientId}/forgot-password`])
      .catch((error: string) => {
        console.log(error)
      })
  }

  onSubmit(): void {
    let value = this.loginForm.value;
    this.loading = true;
    this.buttonText = "Authenticating...";

    // this.procDialogService.open();

    this.authenticationService.doLogin({email: value.email, secret: value.password})
      .then(() => {
        return Promise.resolve();
      })
      .then(() => {
        this.buttonText = "Loading permissions...";
        return Promise.resolve(this.authorizationService.refreshUserGrants());
      })
      .then(() => {
        // get all query params exclude return url
        let queryParams: any = {}
        this.route.snapshot.queryParamMap.keys.filter(key => {
          return key !== 'returnUrl'
        }).forEach(key => {
          queryParams[key] = this.route.snapshot.queryParamMap.get(key);
        });

        let returnUrl = this.route.snapshot.paramMap.get('returnUrl') ?? '';
        // if user not yet verified, take to verification screen first
        if (!this.authenticationService.isVerified) {
          this.authGuard.tryNavigateToVerify(returnUrl, queryParams)
          return;
        }

        this._utilService.navigateWithQueryParams(returnUrl, null, queryParams)
          .catch((reason: string) => {
            console.log(reason)
          });
        return Promise.resolve();
      })
      .finally(() => {
        this.loading = false;
        this.loginForm.get('password')!.setValue(null);
        this.loginForm.get('password')!.setErrors({'login-failed': true});
        this.buttonText = "Login";
        this.procDialogService.close();
      });
  }

  ngOnDestroy(): void {
    this.procDialogService.close();
  }

}
